import { useTranslation } from 'react-i18next';

import type { OvertimeBalanceDto } from '@/types';
import { formatDay, formatHours, formatMonthYear, formatSignedHours } from './overtimeFormat';

interface OvertimeAccountCardProps {
  balance: OvertimeBalanceDto;
}

/**
 * Überstundenkonto als Kopfkarte, in derselben Anatomie wie das Urlaubskonto auf
 * `/absences`: großer Saldo links, die Bestandteile als Kennzahlen rechts.
 */
export function OvertimeAccountCard({ balance }: OvertimeAccountCardProps) {
  const { t } = useTranslation('timeclock');

  // Das Vorzeichen führt die Farbe: eine Zeitschuld ist die einzige Lesart, die auffallen muss.
  const balanceTone = balance.balanceHours < 0 ? 'text-red-600' : 'text-slate-900';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
            {t('overtime.balance')}
          </div>
          <div className="mt-1">
            <span
              className={`font-mono-num text-[32px] sm:text-[36px] font-semibold tabular-nums leading-none ${balanceTone}`}
            >
              {formatSignedHours(balance.balanceHours)}
            </span>
          </div>
          <p className="text-[12px] text-slate-500 mt-1.5 tabular-nums">
            {/* why: bei gesetztem Stichtag sagt die Karte, wo das Konto beginnt — sonst
                fragt sich der Leser, wo die früheren Monate geblieben sind. */}
            {balance.hasConfiguredStartDate
              ? t('overtime.periodFromStart', {
                  start: formatDay(balance.periodStart),
                  end: formatMonthYear(balance.periodEnd),
                })
              : t('overtime.period', {
                  start: formatMonthYear(balance.periodStart),
                  end: formatMonthYear(balance.periodEnd),
                })}
          </p>
        </div>

        <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-2 sm:text-right">
          <Figure label={t('overtime.worked')} value={formatHours(balance.workedHours)} />
          {/* Feiertage und bezahlte Abwesenheiten — nicht gestempelt, aber angerechnet. */}
          <Figure label={t('overtime.credited')} value={formatHours(balance.creditedHours)} />
          <Figure label={t('overtime.target')} value={formatHours(balance.targetHours)} />
          <Figure label={t('overtime.compensation')} value={formatHours(balance.compensationHours)} />
          <Figure
            label={t('overtime.adjustment')}
            value={formatSignedHours(balance.adjustmentHours)}
          />
        </dl>
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 font-mono-num text-[15px] font-medium tabular-nums text-slate-900">
        {value}
      </dd>
    </div>
  );
}
